import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationLinkComponent } from '../navigation-link/navigation-link.component';
import { NavigationConfig, NavigationEvent } from '../navigation-link/interfaces/navigation.interfaces';
import { SalesOfficeNavigationService } from '../../services/navigation/sales-office-navigation.service';
import { NavigableRecord, BaseNavigationQueryParams } from '../../services/navigation/navigable-record.interface';

export type SalesOfficeDisplayMode = 'code' | 'name' | 'both';

export interface SalesOfficeNavigationEvent {
  data: NavigableRecord;
  queryParams: BaseNavigationQueryParams;
  timestamp: Date;
}

@Component({
  selector: 'app-sales-office-navigation',
  standalone: true,
  imports: [CommonModule, NavigationLinkComponent],
  templateUrl: './sales-office-navigation.component.html',
})
export class SalesOfficeNavigationComponent {
  private readonly navService = inject(SalesOfficeNavigationService);

  readonly data = input<NavigableRecord | null | undefined>();
  readonly displayMode = input<SalesOfficeDisplayMode>('both');
  readonly customQueryParams = input<BaseNavigationQueryParams>({});
  readonly openInNewTab = input<boolean>(false);
  readonly customClass = input<string>('');
  readonly showIcon = input<boolean>(true);
  readonly iconPosition = input<'left' | 'right'>('right');
  readonly icon = input<string>('heroicons_outline:rocket-launch');
  readonly showText = input<boolean>(true);
  readonly interactionMode = input<'both' | 'text-only' | 'icon-only'>('icon-only');
  readonly customTooltipText = input<string | undefined>();

  readonly navigate = output<SalesOfficeNavigationEvent>();

  readonly isNavigable = computed(() => this.navService.isNavigable(this.data() ?? null));

  readonly displayText = computed(() => {
    const d = this.data();
    if (!d) return '-';
    const code = d.externalKeys?.code || d.code;
    const name = d.name;
    switch (this.displayMode()) {
      case 'code': return code || '-';
      case 'name': return name || '-';
      case 'both':
        if (code && name) return `${code} - ${name}`;
        return code || name || '-';
      default: return code || '-';
    }
  });

  readonly tooltipText = computed(() => {
    if (!this.isNavigable()) return '';
    return this.customTooltipText() || this.navService.getNavigationDescription();
  });
  readonly displayCode = computed(() => {
    const d = this.data();
    if (!d) return '';
    return d.externalKeys?.code || d.code || '';
  });

  readonly displayName = computed(() => {
    return this.data()?.name || '';
  });

  readonly navigationConfig = computed<NavigationConfig<NavigableRecord, BaseNavigationQueryParams>>(() => ({
    data: this.data(),
    queryParams: this.customQueryParams(),
    openInNewTab: this.openInNewTab(),
  }));

  readonly containerClasses = computed(() =>
    ['sales-office-navigation', this.isNavigable() ? 'navigable' : 'non-navigable', this.customClass()]
      .filter(cls => cls.length > 0).join(' ')
  );

  handleNavigation(navigationEvent: NavigationEvent<NavigableRecord, BaseNavigationQueryParams>): void {
    if (!this.isNavigable()) return;
    this.navigate.emit({
      data: navigationEvent.config.data,
      queryParams: navigationEvent.config.queryParams || {},
      timestamp: navigationEvent.timestamp,
    });
    this.navService.navigateToDetail(
      navigationEvent.config.data,
      navigationEvent.config.queryParams,
      navigationEvent.config.openInNewTab,
    );
  }
}
